import { useMutation } from '@apollo/client';
import { Alert, Button, Divider, Form, message, Radio, Tooltip } from 'antd';
import React, { useState } from 'react';
import { formValidatorRules } from '../../common/utils';
import ModalComponent from '../ModalComponent';
import LibraryStaffSelect from './LibraryStaffSelect';
import {
  BULK_ROLE_LOCKED_EMPTY_MESSAGES,
  BULK_ROLE_LOCKED_NOTES,
  DIFFERENT_LIBRARIES_MESSAGE,
  ORDER_ITEM_STAFF_ROLES,
  canAssignRoleToItem,
} from './constants';
import { REASSIGN_ORDER_ITEM_STAFF } from './graphql/Mutations';

const { Item } = Form;
const { required } = formValidatorRules;

const ROLE_OPTIONS = Object.values(ORDER_ITEM_STAFF_ROLES).map((role) => ({
  label: role?.label,
  value: role?.key,
}));

const BulkAssignStaffButton = ({
  hasSelection,
  libraryIds = [],
  isLibrary = false,
  getOrderItems,
  onSuccess,
  className = 'mr-8',
}) => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isValidateTrigger, setIsValidateTrigger] = useState(false);
  const [isCollectingIds, setIsCollectingIds] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [role, setRole] = useState(ORDER_ITEM_STAFF_ROLES?.PICKER?.key);

  const hasMultipleLibraries = !isLibrary && libraryIds?.length > 1;
  const staffRole = Object.values(ORDER_ITEM_STAFF_ROLES).find(
    (item) => item?.key === role,
  );

  const [
    reassignOrderItemStaff,
    { loading },
  ] = useMutation(REASSIGN_ORDER_ITEM_STAFF, { onError() {} });

  const openModal = () => {
    setRole(ORDER_ITEM_STAFF_ROLES?.PICKER?.key);
    setSelectedUser(null);
    setIsValidateTrigger(false);
    setIsModalOpen(true);
  };

  const handleFinish = async ({ userId }) => {
    setIsCollectingIds(true);
    const orderItems = await getOrderItems();
    setIsCollectingIds(false);
    const orderItemIds = (orderItems || [])
      ?.filter((item) => canAssignRoleToItem(item, role))
      ?.map((item) => item?.id);
    if (!orderItemIds?.length) {
      if (orderItems?.length && BULK_ROLE_LOCKED_EMPTY_MESSAGES?.[role]) {
        message.warning(BULK_ROLE_LOCKED_EMPTY_MESSAGES?.[role]);
      }
      return;
    }

    const response = await reassignOrderItemStaff({
      variables: {
        where: { ids: orderItemIds },
        data: { role, userId },
      },
    });
    if (response?.data) {
      onSuccess?.(orderItemIds, staffRole?.field, selectedUser);
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <Tooltip title={hasMultipleLibraries ? DIFFERENT_LIBRARIES_MESSAGE : ''}>
        <Button
          type="primary"
          className={className}
          disabled={!hasSelection || hasMultipleLibraries}
          onClick={openModal}
        >
          Assign Picker / Packer
        </Button>
      </Tooltip>
      <ModalComponent
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={form?.submit}
        title="Assign Picker / Packer"
        okText="Save"
        confirmLoading={loading || isCollectingIds}
        destroyOnClose
      >
        <Divider />
        <Form
          form={form}
          initialValues={{ role: ORDER_ITEM_STAFF_ROLES?.PICKER?.key }}
          onFinish={handleFinish}
          autoComplete="off"
          layout="vertical"
          validateTrigger={isValidateTrigger ? 'onChange' : 'onSubmit'}
          onFinishFailed={() => setIsValidateTrigger(true)}
        >
          <Item label="Assign As" name="role">
            <Radio.Group
              options={ROLE_OPTIONS}
              onChange={(e) => {
                setRole(e?.target?.value);
                setSelectedUser(null);
                form?.setFieldsValue({ userId: undefined });
              }}
            />
          </Item>
          {BULK_ROLE_LOCKED_NOTES?.[role] && (
            <Alert
              className="mb-16"
              type="info"
              showIcon
              message={BULK_ROLE_LOCKED_NOTES?.[role]}
            />
          )}
          <Item
            label={staffRole?.label}
            name="userId"
            rules={[
              { ...required, message: `Please Select ${staffRole?.label}!` },
            ]}
          >
            <LibraryStaffSelect
              key={role}
              role={role}
              libraryId={libraryIds?.[0]}
              isLibrary={isLibrary}
              placeholder={`Select ${staffRole?.label}`}
              onChange={(_, option) => setSelectedUser(option?.user || null)}
            />
          </Item>
        </Form>
      </ModalComponent>
    </>
  );
};

export default BulkAssignStaffButton;
